import classNames from '@/common/utils/classNames';
import { ForwardedRef, forwardRef, InputHTMLAttributes, useId, useState } from 'react';
import EyeIcon from '../../icons/eye';
import EyeSlashIcon from '../../icons/eyeSlash';

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>, 'size'>;

export interface TextFieldProps extends InputProps {
  label?: string;
  size?: 'small' | 'medium' | 'large';
  multiLine?: boolean;
  helperText?: string;
  error?: boolean;
  classNameWrapper?: string;
}

// eslint-disable-next-line react/display-name
export const TextField = forwardRef((props: TextFieldProps, ref: ForwardedRef<any>) => {
  const { label, className, classNameWrapper, size = 'medium', multiLine = false, helperText, error = false, ...inputProps } = props;
  const [isObscured, setIsObscured] = useState(inputProps.type === 'password');
  const generatedId = useId();
  const id = inputProps.id ?? generatedId;
  const helperId = `${id}-help`;
  const describedBy = [inputProps['aria-describedby'], helperText && helperId].filter(Boolean).join(' ') || undefined;

  const Component = multiLine ? 'textarea' : 'input';

  return (
    <div
      className={classNames(
        'patient-field flex flex-col gap-2 w-full border-slate-300 outline-primary',
        {
          'text-red-600 border-red-200 !outline-red-200 ': error,
        },
        classNameWrapper,
      )}
    >
      {label && (
        <label htmlFor={id} className="patient-field-label">
          {label}
        </label>
      )}
      <div className="relative w-full border-inherit outline-inherit">
        <Component
          ref={ref}
          autoComplete="off"
          className={classNames('patient-field-control border border-solid border-inherit outline-inherit w-full', className, {
            'pl-11': inputProps.type === 'password',
          })}
          {...inputProps}
          id={id}
          data-field-size={size}
          data-field-password={inputProps.type === 'password' || undefined}
          aria-describedby={describedBy}
          aria-invalid={error || inputProps['aria-invalid']}
          type={inputProps.type === 'password' ? (isObscured ? 'password' : 'text') : inputProps.type}
        />
        {inputProps.type === 'password' && (
          <button
            type="button"
            className="patient-field-action absolute left-1 top-1/2 -translate-y-1/2"
            disabled={inputProps.disabled}
            aria-label={isObscured ? 'نمایش رمز عبور' : 'پنهان کردن رمز عبور'}
            aria-pressed={!isObscured}
            onClick={() => setIsObscured(value => !value)}
          >
            {isObscured ? <EyeIcon aria-hidden className="w-5 h-5" /> : <EyeSlashIcon aria-hidden className="w-5 h-5" />}
          </button>
        )}
      </div>
      {helperText && (
        <span id={helperId} className="patient-field-help">
          {helperText}
        </span>
      )}
    </div>
  );
});

export default TextField;
